import { destroyEstablishment, useEstablishments } from '@gozoki/api';
import { Button, TextField } from '@mui/material';
import { GridColDef, GridRowId } from '@mui/x-data-grid';
import { useCallback, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ConfirmResourceDeletionButton } from '../../components/communication/ConfirmDeletionButton';
import SimpleDataGrid from '../../components/containers/SimpleDataGrid';
import { ROUTE_BUILDER } from '../../utils/constants/routes.constants';
import { useRights } from '../../utils/hooks';

const establishmentsColDefs: GridColDef[] = [
    {
        field: 'label',
        headerName: 'Label',
        flex: 1,
    },
    {
        field: 'reference',
        headerName: 'Référence',
        flex: 1,
        renderCell: ({ value }) => {
            return value ?? <i style={{ color: 'red' }}>Non renseigné</i>;
        },
    },
    {
        field: 'seller',
        headerName: 'Vendeur',
        flex: 3,
        valueFormatter: ({ value }) => value.label,
    },
    {
        field: 'address',
        headerName: 'Adresse',
        flex: 2,
        renderCell: ({ value }) => {
            return value ? (
                `${value.address} ${value.city} ${value.postalCode}`
            ) : (
                <i style={{ color: 'red' }}>Non renseigné</i>
            );
        },
    },
];

export const EstablishmentsPanel = () => {
    const { data: establishments, isLoading, refetch } = useEstablishments();
    const navigate = useNavigate();
    const [establishmentsSearch, setEstablishmentsSearch] = useState('');
    const [selectedEstablishments, setSelectedEstablishments] = useState<number[]>([]);
    const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);
    const { mutate: deleteEstablishment } = useMutation({
        mutationFn: destroyEstablishment,
        onSuccess: () => {
            setSelectedEstablishments([]);
            setSelectionModel([]);
            refetch();
        },
    });
    const handleDeleteEstablishment = useCallback(() => {
        deleteEstablishment(selectedEstablishments[0]);
    }, [selectedEstablishments, deleteEstablishment]);

    const handleUpdateEstablishment = useCallback(() => {
        navigate(ROUTE_BUILDER.updateEstablishment(selectedEstablishments[0]));
    }, [selectedEstablishments, navigate]);

    const isAdmin = useRights(['ADMIN']);

    if (!establishments || isLoading) return <div>Loading</div>;

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', padding: '16px' }}>
                <TextField
                    variant="outlined"
                    label="Recherche"
                    value={establishmentsSearch}
                    onChange={(e) => setEstablishmentsSearch(e.target.value)}
                />
                <div style={{ flexGrow: 1 }} />
                <ConfirmResourceDeletionButton
                    handleResourceDeletion={async () => handleDeleteEstablishment}
                    disabled={!isAdmin}
                />
                <Button
                    variant="contained"
                    sx={{ height: '36px' }}
                    onClick={handleUpdateEstablishment}
                    disabled={!selectedEstablishments.length}
                >
                    {isAdmin ? 'Modifier' : 'Voir'}
                </Button>
            </div>
            <SimpleDataGrid
                sx={{
                    '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
                        display: 'none',
                    },
                }}
                rowHeight={100} // For image display
                columns={establishmentsColDefs}
                rows={establishments}
                loading={isLoading}
                rowSelectionModel={selectionModel}
                onRowSelectionModelChange={(selection) => {
                    if (selection.length > 1) {
                        const selectionSet = new Set(selectionModel);
                        const result = selection.filter((s) => !selectionSet.has(s));
                        setSelectionModel(result);
                        setSelectedEstablishments([selection[selection.length - 1]] as number[]);
                    } else {
                        setSelectionModel(selection);
                        setSelectedEstablishments([selection[selection.length - 1]] as number[]);
                    }
                }}
            />
        </>
    );
};
